/*
 * Settings: how the knowledge base presents itself.
 *
 * Four sections, all writing the one row of kb.SiteSettings. "Knowledge Base" is
 * the name and face: the icon shown top-left instead of the built-in "KB" mark,
 * and the site title - the header of every page and the browser tab. It was
 * called "Website" until 2026-10-15, when the owner had it renamed - "The shown
 * settings are for the knowledge base itself (i.e. articles)" - first to
 * "Articles" and then, the same day, to "Knowledge Base". "Articles" and
 * "Release Notes" (both 2026-10-15) are the two pages' own titles and the lines
 * under them, one card component twice (PageHeadingSection); the articles
 * page's line is the subtitle that sat in the first card until the owner moved
 * it - it was always that page's line - and since then the site title heads no
 * page. "Theme" is the colour: four named token sets, each shown as a thumbnail
 * of a real article, previewed on this page by clicking and applied for every
 * reader by a button.
 *
 * TWO KINDS OF SAVE, BECAUSE THE ROW HAS TWO KINDS OF CONTENT
 *
 *   The titles are forms: typed, then saved, gated on the rowversion the page
 *   loaded so two administrators cannot silently overwrite each other. Same
 *   shape as the article editor's Properties card. Three of them since
 *   2026-10-15 - the site title, and each page's title and line - on the same
 *   rowversion: each card sends the token from the row it was handed, and every
 *   write hands every card the new one, so saving one card and then another
 *   does not conflict with itself.
 *
 *   The icon is a file: chosen, then uploaded at once, replacing whatever was
 *   there. There is no draft state to save - an icon is either the old one or the
 *   new one - so it does not wait for the Save button, and the "Remove" beside it
 *   is likewise immediate. Each of those writes returns the whole row, so the
 *   form's rowversion moves with it and the next title save does not conflict
 *   with the page's own upload.
 *
 * THE TEXT FIELDS RE-SEED ONLY WHEN THE SERVER'S TEXT CHANGES. An icon upload
 * returns the row with the title as stored, not as typed; re-seeding on every
 * response would throw away a half-typed title because a logo was chosen - or
 * because another card was saved. So each card's effect watches its own
 * texts: after this page's own
 * save they equal what was typed, after an icon upload they are unchanged, and
 * only a reload that brought somebody else's edit resets the boxes.
 *
 * ONE WRITE AT A TIME, ACROSS THE PAGE (the review of 2026-10-15). Every write
 * moves the row's one rowversion, and the text cards send the token they were
 * last handed: a second write begun before the first came back would carry the
 * spent token and be refused as "someone else" - the administrator's own save
 * blamed on a stranger - and responses arriving out of order could leave the
 * page holding the older token. So every write goes through `track`, and every
 * Save, the icon controls and Apply theme wait while any write is in flight.
 * Typing does not wait; only the card that is saving holds its fields.
 *
 * EACH CARD IS A SECTION NAMED BY ITS HEADING, because two cards carry a
 * "Title" and a "Subtitle", and three a "Save": in a screen reader's list of
 * fields or landmarks the card's name is what tells them apart.
 *
 * THE LOADED ROW AND EVERY WRITE ARE PUSHED INTO THE BRAND AS AUTHORITATIVE. The
 * admin row arrives no-store; the public bootstrap and /public/site the shells
 * otherwise read from can be a minute stale. So the header above this page is
 * seeded from the row the form shows and can never contradict it, and a later
 * cached bootstrap cannot overwrite what was just saved - see SiteBranding. The
 * notice tells the truth about readers: they pick the change up as the public
 * cache expires, a minute by default.
 *
 * Its words come from the chosen language's dictionary, `t.admin.site` (S48);
 * the headings it saves are content, and stay as the administrator wrote them.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminSiteSettings } from '@accotool-kb/shared';
import {
  ARTICLE_IMAGE_ACCEPT_ATTRIBUTE,
  SITE_ICON_MAX_BYTES,
  SITE_SUBTITLE_MAX_LENGTH,
  SITE_THEME_OPTIONS,
  SITE_TITLE_MAX_LENGTH,
  inspectArticleImageBytes,
  siteIconUrl,
  siteThemeLabel,
} from '@accotool-kb/shared';
import { useAdminApi } from '../auth/AuthProvider.js';
import { useAction, useAsync } from '../hooks/useAsync.js';
import { DateTime, ErrorBanner, Field, Loading } from '../components/ui.js';
import { ApiError } from '../api/client.js';
import { getArticleBySlug, listArticles } from '../api/publicApi.js';
import { LANGUAGES, languageFor, useLanguage, useMessages, type LanguageCode } from '../i18n/language.js';
import { richText } from '../i18n/richText.js';
import { useSiteBranding } from '../site/SiteBranding.js';
import { Flag } from '../site/LanguageSwitch.js';
import { articlesHeading } from '../public/pageHeadings.js';
import {
  ThemePreview,
  samplePreviewArticle,
  type ThemePreviewArticle,
} from './components/ThemePreview.js';

/** Runs one write and counts it as in flight - see ONE WRITE AT A TIME. */
type TrackWrite = <T>(write: () => Promise<T>) => Promise<T>;

export function SettingsPage(): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.settings;
  const api = useAdminApi();
  const branding = useSiteBranding();
  const state = useAsync(() => api.getSiteSettings(), []);

  const [writes, setWrites] = useState(0);
  const track = useCallback<TrackWrite>(async (write) => {
    setWrites((count) => count + 1);
    try {
      return await write();
    } finally {
      setWrites((count) => count - 1);
    }
  }, []);
  const writing = writes > 0;

  // The admin row is the truth. Hand it to the brand as authoritative so the header
  // above this page shows what the form below it shows, whatever a cached public
  // response said a moment earlier.
  useEffect(() => {
    if (state.data) branding.setSite(state.data, { authoritative: true });
  }, [state.data, branding]);

  if (state.loading && !state.data) return <Loading label={s.loading} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Loading />;

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{s.title}</h1>
          <p className="muted">{s.intro}</p>
        </div>
      </div>

      <KnowledgeBaseSection
        settings={state.data}
        writing={writing}
        track={track}
        onSaved={(next) => {
          state.setData(next);
          branding.setSite(next, { authoritative: true });
        }}
        onReload={state.reload}
      />

      <PageHeadingSection
        name={t.nav.articles}
        idPrefix="articles"
        savedTitle={state.data.articlesTitle}
        savedSubtitle={state.data.subtitle}
        savedTitleDa={state.data.articlesTitleDa ?? null}
        savedSubtitleDa={state.data.articlesSubtitleDa ?? null}
        rowVersion={state.data.rowVersion}
        saveHeading={api.updateArticlesSettings}
        titleHint={s.articles.titleHint}
        subtitleHint={s.articles.subtitleHint}
        savedNotice={s.articles.saved}
        writing={writing}
        track={track}
        onSaved={(next) => {
          state.setData(next);
          branding.setSite(next, { authoritative: true });
        }}
        onReload={state.reload}
      />

      <PageHeadingSection
        name={t.nav.releaseNotes}
        idPrefix="release-notes"
        savedTitle={state.data.releaseNotesTitle}
        savedSubtitle={state.data.releaseNotesSubtitle}
        savedTitleDa={state.data.releaseNotesTitleDa ?? null}
        savedSubtitleDa={state.data.releaseNotesSubtitleDa ?? null}
        rowVersion={state.data.rowVersion}
        saveHeading={api.updateReleaseNotesSettings}
        titleHint={s.releaseNotes.titleHint}
        subtitleHint={s.releaseNotes.subtitleHint}
        savedNotice={s.releaseNotes.saved}
        writing={writing}
        track={track}
        onSaved={(next) => {
          state.setData(next);
          branding.setSite(next, { authoritative: true });
        }}
        onReload={state.reload}
      />

      <ThemeSection
        settings={state.data}
        writing={writing}
        track={track}
        onSaved={(next) => {
          state.setData(next);
          branding.setSite(next, { authoritative: true });
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Knowledge Base ("Website", then briefly "Articles", until 2026-10-15): icon and site title
// ---------------------------------------------------------------------------

function KnowledgeBaseSection({
  settings,
  writing,
  track,
  onSaved,
  onReload,
}: {
  settings: AdminSiteSettings;
  writing: boolean;
  track: TrackWrite;
  onSaved: (next: AdminSiteSettings) => void;
  onReload: () => void;
}): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.settings;
  const api = useAdminApi();
  const save = useAction();
  const iconAction = useAction();

  const [title, setTitle] = useState(settings.title);
  const [notice, setNotice] = useState<string | null>(null);
  const [iconError, setIconError] = useState<string | null>(null);
  // True while a chosen file's bytes are being read for the local gate - the one
  // moment neither action is running yet, and a second pick could start a second
  // upload racing the first.
  const [reading, setReading] = useState(false);

  // See the file header: the server's TEXT is the trigger, not its rowversion.
  useEffect(() => {
    setTitle(settings.title);
  }, [settings.title]);

  const fileInput = useRef<HTMLInputElement>(null);

  const trimmedTitle = title.trim();
  const titleError =
    trimmedTitle.length === 0
      ? s.titleRequired
      : trimmedTitle.length > SITE_TITLE_MAX_LENGTH
        ? s.tooLong(SITE_TITLE_MAX_LENGTH)
        : undefined;

  const dirty = trimmedTitle !== settings.title;

  const submit = async () => {
    if (titleError || !dirty) return;

    const next = await save.run(() =>
      track(() =>
        api.updateSiteSettings({
          title: trimmedTitle,
          // From the prop, not from state: an icon upload moves it, and the prop is
          // what the upload's response updated.
          rowVersion: settings.rowVersion,
        }),
      ),
    );
    if (!next) return;

    onSaved(next);
    setNotice(s.siteTitleSaved);
  };

  const chooseIcon = async (file: File | undefined) => {
    setIconError(null);
    // Reset the control FIRST, whatever happens next. A file input still holding a
    // rejected file fires no change event when the same path is picked again, so
    // an administrator who shrinks the file in place and re-selects it would get
    // nothing - not even the old error going away. The File object already in
    // hand is unaffected by clearing the control.
    if (fileInput.current) fileInput.current.value = '';
    if (!file) return;

    // The same gate the server runs, run first so an author is not made to wait
    // for an upload that was never going to be accepted. The server still decides.
    if (file.size > SITE_ICON_MAX_BYTES) {
      setIconError(s.iconTooLarge(t.article.byteSize(file.size), t.admin.rules.siteIconSizeLimit));
      return;
    }

    setReading(true);
    let bytes: ArrayBuffer;
    try {
      bytes = await file.arrayBuffer();
    } catch {
      setReading(false);
      setIconError(s.iconUnreadable);
      return;
    }
    setReading(false);

    const inspection = inspectArticleImageBytes(bytes);
    if (!inspection.ok) {
      setIconError(inspection.message ?? s.iconNotAnImage);
      return;
    }

    const next = await iconAction.run(() => track(() => api.uploadSiteIcon(file)));
    if (!next) return;

    onSaved(next);
    setNotice(s.iconUpdated);
  };

  const removeIcon = async () => {
    setIconError(null);
    const next = await iconAction.run(() => track(() => api.deleteSiteIcon()));
    if (!next) return;

    onSaved(next);
    setNotice(s.iconRemoved);
  };

  // `writing` covers this card's own writes and every other card's.
  const busy = writing || reading;

  return (
    <section className="card" aria-labelledby="settings-knowledge-base">
      <div className="card-header">
        <h3 id="settings-knowledge-base">{s.knowledgeBase}</h3>
        <span className="faint">
          {richText(s.lastChanged, {
            date: <DateTime value={settings.modifiedAtUtc} />,
            name: settings.modifiedBy,
          })}
        </span>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {save.error && (
        <ErrorBanner
          error={save.error}
          onDismiss={save.clearError}
          {...(save.error instanceof ApiError && save.error.isConcurrencyConflict
            ? { onRetry: onReload }
            : {})}
        />
      )}
      {iconAction.error && <ErrorBanner error={iconAction.error} onDismiss={iconAction.clearError} />}

      {/* ------------------------------------------------------------ icon */}
      <div className="field">
        <label htmlFor="site-icon">{s.icon}</label>
        <div className="site-icon-row">
          <span className="site-icon-preview" aria-hidden="true">
            {settings.icon ? (
              <img className="brand-icon" src={siteIconUrl(settings.icon.contentHash)} alt="" />
            ) : (
              <span className="brand-mark">KB</span>
            )}
          </span>

          <div className="site-icon-detail">
            <span className="faint">
              {settings.icon
                ? `${settings.icon.contentType.replace('image/', '').toUpperCase()} · ${t.article.byteSize(settings.icon.byteLength)}`
                : s.noIcon}
            </span>
            <div className="button-row">
              {/*
                A real file input, labelled by the visible button through its
                id, rather than a hidden input clicked from a button: the browser's
                own control is what a keyboard and a screen reader already know.
              */}
              <input
                ref={fileInput}
                id="site-icon"
                type="file"
                accept={ARTICLE_IMAGE_ACCEPT_ATTRIBUTE}
                disabled={busy}
                onChange={(event) => void chooseIcon(event.target.files?.[0])}
              />
              {settings.icon && (
                <button
                  type="button"
                  className="button button-small button-quiet"
                  onClick={() => void removeIcon()}
                  disabled={busy}
                >
                  {s.removeIcon}
                </button>
              )}
            </div>
          </div>
        </div>
        {iconError ? (
          <p className="field-error" role="alert">
            {iconError}
          </p>
        ) : (
          <p className="field-hint">{s.iconHint(t.article.byteSize(SITE_ICON_MAX_BYTES))}</p>
        )}
      </div>

      {/* ------------------------------------------------------------ title */}
      {/*
        The site's name and nothing else since 2026-10-15 - the header and the
        browser tab. The articles page has its own title and line, in the next
        card: the subtitle that used to sit here was always that page's line.
      */}
      <Field label={s.siteTitle} htmlFor="site-title" required error={titleError}
        hint={s.siteTitleHint}>
        {/*
          Disabled while a save is in flight, like the article editor's text. The
          server answers with the stored (trimmed) text and the field re-seeds
          from it, so a character typed during the round trip would be discarded
          by that re-seed; refusing input for the second the save takes is the
          honest alternative.
        */}
        <input
          id="site-title"
          type="text"
          value={title}
          maxLength={SITE_TITLE_MAX_LENGTH}
          disabled={save.running}
          onChange={(event) => setTitle(event.target.value)}
        />
      </Field>

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void submit()}
          disabled={!dirty || busy || Boolean(titleError)}
        >
          {save.running ? s.saving : s.save}
        </button>
        {dirty && <span className="unsaved">{s.unsaved}</span>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Articles and Release Notes: a page's own title and the line under it (2026-10-15)
// ---------------------------------------------------------------------------

/*
 * One card, twice. The owner first asked for the release-note list - "Ensure that
 * similar can be configured for the Release Notes site title and subtitle" - and
 * then for the articles page: "Similar to the new Release Notes card, ensure that
 * Article has a similar card. The title from this card should be used in article
 * site pages", with the site title left to the header and the tab. So each card
 * holds its page's heading - also that page's browser tab and the link back to it
 * from every item - and the line under it: the title required, never blank, at
 * most 200 characters; the subtitle at most 500 and allowed to be empty; Save
 * gated on the row's rowversion, the fields re-seeded only when the server's text
 * for THESE two changes. The articles page's line is the old site subtitle,
 * which was always that page's line. The header's "Articles" and "Release Notes"
 * links keep their names.
 *
 * No "you see it now": the header and tab above this page are the branding
 * provider's, but the two pages read the public bootstrap, so a new heading
 * reaches every reader - this one included - as that cache expires.
 *
 * IN ENGLISH AND DANISH (2026-10-29, S49), at the owner's request, on the design
 * the owner approved from a mockup: the English fields as they were, required
 * title and all, and beside them an optional Danish title and line. An empty
 * Danish field is "use the English" - NULL on the row - and its placeholder
 * says which English it stands for; "Readers see" shows what each language's
 * readers get. The Danish fields are labelled "Danish title" and "Danish
 * subtitle" rather than "Title" again, so a screen reader - and a test - can
 * tell the two titles apart. Save sends both languages; a request without the Danish
 * keeps what is stored, which is what a page loaded before the fields existed
 * sends.
 */
function PageHeadingSection({
  name,
  idPrefix,
  savedTitle,
  savedSubtitle,
  savedTitleDa,
  savedSubtitleDa,
  rowVersion,
  saveHeading,
  titleHint,
  subtitleHint,
  savedNotice,
  writing,
  track,
  onSaved,
  onReload,
}: {
  /** The card's heading - and the section's accessible name. */
  name: string;
  /** Prefixes the section's and the fields' ids. */
  idPrefix: string;
  savedTitle: string;
  savedSubtitle: string;
  /** The Danish pair as stored - null where none is written. */
  savedTitleDa: string | null;
  savedSubtitleDa: string | null;
  rowVersion: string;
  saveHeading: (input: {
    title: string;
    subtitle: string;
    danish: { title: string | null; subtitle: string | null };
    rowVersion: string;
  }) => Promise<AdminSiteSettings>;
  titleHint: string;
  subtitleHint: string;
  savedNotice: string;
  writing: boolean;
  track: TrackWrite;
  onSaved: (next: AdminSiteSettings) => void;
  onReload: () => void;
}): JSX.Element {
  const t = useMessages();
  const s = t.admin.site.settings;
  const save = useAction();

  const [title, setTitle] = useState(savedTitle);
  const [subtitle, setSubtitle] = useState(savedSubtitle);
  const [titleDa, setTitleDa] = useState(savedTitleDa ?? '');
  const [subtitleDa, setSubtitleDa] = useState(savedSubtitleDa ?? '');
  const [notice, setNotice] = useState<string | null>(null);

  // The server's text for this card is the trigger - see the file header.
  useEffect(() => {
    setTitle(savedTitle);
    setSubtitle(savedSubtitle);
    setTitleDa(savedTitleDa ?? '');
    setSubtitleDa(savedSubtitleDa ?? '');
  }, [savedTitle, savedSubtitle, savedTitleDa, savedSubtitleDa]);

  const trimmedTitle = title.trim();
  const titleError =
    trimmedTitle.length === 0
      ? s.titleRequired
      : trimmedTitle.length > SITE_TITLE_MAX_LENGTH
        ? s.tooLong(SITE_TITLE_MAX_LENGTH)
        : undefined;
  const subtitleError =
    subtitle.trim().length > SITE_SUBTITLE_MAX_LENGTH
      ? s.tooLong(SITE_SUBTITLE_MAX_LENGTH)
      : undefined;

  // The Danish pair: empty is none, so only a length can be wrong.
  const trimmedTitleDa = titleDa.trim();
  const trimmedSubtitleDa = subtitleDa.trim();
  const titleDaError =
    trimmedTitleDa.length > SITE_TITLE_MAX_LENGTH ? s.tooLong(SITE_TITLE_MAX_LENGTH) : undefined;
  const subtitleDaError =
    trimmedSubtitleDa.length > SITE_SUBTITLE_MAX_LENGTH ? s.tooLong(SITE_SUBTITLE_MAX_LENGTH) : undefined;
  const invalid = Boolean(titleError || subtitleError || titleDaError || subtitleDaError);

  const dirty =
    trimmedTitle !== savedTitle ||
    subtitle.trim() !== savedSubtitle ||
    trimmedTitleDa !== (savedTitleDa ?? '') ||
    trimmedSubtitleDa !== (savedSubtitleDa ?? '');

  const submit = async () => {
    if (invalid || !dirty) return;

    const next = await save.run(() =>
      track(() =>
        saveHeading({
          title: trimmedTitle,
          subtitle: subtitle.trim(),
          danish: { title: trimmedTitleDa || null, subtitle: trimmedSubtitleDa || null },
          // From the prop: a save in another card, or an icon upload, moves it.
          rowVersion,
        }),
      ),
    );
    if (!next) return;

    onSaved(next);
    setNotice(savedNotice);
  };

  const headingId = `settings-${idPrefix}`;

  return (
    <section className="card" aria-labelledby={headingId}>
      <div className="card-header">
        <h3 id={headingId}>{name}</h3>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {save.error && (
        <ErrorBanner
          error={save.error}
          onDismiss={save.clearError}
          {...(save.error instanceof ApiError && save.error.isConcurrencyConflict
            ? { onRetry: onReload }
            : {})}
        />
      )}

      <div className="heading-languages">
        <div>
          <LanguageColumnName code="en-us" />
          <Field label={s.headingTitle} htmlFor={`${idPrefix}-title`} required error={titleError} hint={titleHint}>
            {/* Disabled while saving, for the reason the Knowledge Base card gives. */}
            <input
              id={`${idPrefix}-title`}
              type="text"
              value={title}
              maxLength={SITE_TITLE_MAX_LENGTH}
              disabled={save.running}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>

          <Field label={s.headingSubtitle} htmlFor={`${idPrefix}-subtitle`} error={subtitleError} hint={subtitleHint}>
            <input
              id={`${idPrefix}-subtitle`}
              type="text"
              value={subtitle}
              maxLength={SITE_SUBTITLE_MAX_LENGTH}
              disabled={save.running}
              onChange={(event) => setSubtitle(event.target.value)}
            />
          </Field>
        </div>

        <div>
          <LanguageColumnName code="da-dk" />
          <Field label={s.headingTitleDanish} htmlFor={`${idPrefix}-title-da`} error={titleDaError} hint={s.danishHint}>
            <input
              id={`${idPrefix}-title-da`}
              type="text"
              lang="da"
              value={titleDa}
              placeholder={s.usesEnglish(trimmedTitle)}
              maxLength={SITE_TITLE_MAX_LENGTH}
              disabled={save.running}
              onChange={(event) => setTitleDa(event.target.value)}
            />
          </Field>

          <Field label={s.headingSubtitleDanish} htmlFor={`${idPrefix}-subtitle-da`} error={subtitleDaError}>
            <input
              id={`${idPrefix}-subtitle-da`}
              type="text"
              lang="da"
              value={subtitleDa}
              placeholder={subtitle.trim() ? s.usesEnglish(subtitle.trim()) : s.usesEnglishNone}
              maxLength={SITE_SUBTITLE_MAX_LENGTH}
              disabled={save.running}
              onChange={(event) => setSubtitleDa(event.target.value)}
            />
          </Field>
        </div>
      </div>

      {/* What each language's readers get as the heading, as typed - the Danish
          falling back to the English while it is empty. */}
      <p className="heading-preview faint">
        <span>{s.readersSee}</span>
        {LANGUAGES.map((language) => (
          <span key={language.code}>
            {language.short}: {(language.code === 'da-dk' && trimmedTitleDa) || trimmedTitle}
          </span>
        ))}
      </p>

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void submit()}
          disabled={!dirty || writing || invalid}
        >
          {save.running ? s.saving : s.save}
        </button>
        {dirty && <span className="unsaved">{s.unsaved}</span>}
      </div>
    </section>
  );
}

/** A page card's language column heading: the flag and the language's own name, as the dropdown has it. */
function LanguageColumnName({ code }: { code: LanguageCode }): JSX.Element {
  const language = languageFor(code);
  return (
    <p className="heading-language" lang={language.htmlLang}>
      <Flag code={code} />
      {language.name} ({language.short})
    </p>
  );
}

// ---------------------------------------------------------------------------
// Theme: four thumbnails of one article, a click to preview, a button to apply
// ---------------------------------------------------------------------------

/**
 * A random published article, for the thumbnails - the same one under all four
 * themes, so the eye compares colour and nothing else. Chosen once per visit to
 * the page, and from ALL published articles: one read learns how many there are,
 * a second reads a page of one at a random offset, so an old article is as
 * likely as the newest. When the knowledge base has no published article yet,
 * or a read fails, the built-in sample stands in; the thumbnails are about the
 * theme, not the text, and an empty preview would say nothing.
 * Null means the sample, which the section draws in the chosen language.
 */
async function loadSampleArticle(): Promise<ThemePreviewArticle | null> {
  try {
    const filter = { products: [], categories: [], q: '', pageSize: 1, sort: 'recent' as const };
    const probe = await listArticles({ ...filter, page: 1 });
    if (probe.total < 1) return null;

    const page = 1 + Math.floor(Math.random() * probe.total);
    const list = page === 1 ? probe : await listArticles({ ...filter, page });
    const pick = list.items[0];
    if (!pick) return null;

    // Both halves of the address, because that is what addresses an article now -
    // the list row carries its product for exactly this reason.
    const article = await getArticleBySlug(pick.product.slug, pick.slug);
    return {
      address: { productSlug: article.product.slug, slug: article.slug },
      title: article.title,
      publishAtUtc: article.publishAtUtc,
      productName: article.product.name,
      categoryTags: article.categoryTags,
      content: article.content,
      linkTargets: article.linkTargets,
    };
  } catch {
    return null;
  }
}

/*
 * PREVIEW IS LOCAL, APPLY IS GLOBAL, AND THE TWO ARE KEPT APART ON PURPOSE.
 * Clicking a thumbnail sets the provider's preview theme, which re-themes this
 * page - header, cards, buttons - and nothing else: no request is made, no other
 * reader sees it, and leaving the page drops it (the effect below clears it on
 * unmount). "Apply theme" is the save: one PUT, the row comes back, the provider
 * applies the saved theme for real and the preview is released. The thumbnails
 * themselves are unaffected by either, because each carries its own data-theme.
 */
function ThemeSection({
  settings,
  writing,
  track,
  onSaved,
}: {
  settings: AdminSiteSettings;
  writing: boolean;
  track: TrackWrite;
  onSaved: (next: AdminSiteSettings) => void;
}): JSX.Element {
  const { language } = useLanguage();
  const t = language.messages;
  const s = t.admin.site.settings;
  const api = useAdminApi();
  const { previewTheme, setPreviewTheme } = useSiteBranding();
  const apply = useAction();
  const [notice, setNotice] = useState<string | null>(null);

  const sample = useAsync(() => loadSampleArticle(), []);
  const article = sample.data ?? samplePreviewArticle(t.admin.site);

  // Leaving the page takes an unapplied preview with it. setPreviewTheme is a
  // stable callback, so this runs exactly once, on unmount.
  useEffect(() => () => setPreviewTheme(null), [setPreviewTheme]);

  const previewing = previewTheme ?? settings.theme;
  const dirty = previewing !== settings.theme;

  const applyTheme = async () => {
    if (!dirty) return;
    const next = await apply.run(() => track(() => api.updateSiteTheme(previewing)));
    if (!next) return;

    onSaved(next);
    setPreviewTheme(null);
    setNotice(s.themeApplied(siteThemeLabel(next.theme)));
  };

  return (
    <section className="card" aria-labelledby="settings-theme">
      <div className="card-header">
        <h3 id="settings-theme">{s.theme}</h3>
        <span className="faint">{s.currentTheme(siteThemeLabel(settings.theme))}</span>
      </div>

      {notice && (
        <div className="notice notice-success" role="status">
          <div className="notice-body">{notice}</div>
          <div className="notice-actions">
            <button type="button" className="button button-quiet" onClick={() => setNotice(null)}>
              {t.common.dismiss}
            </button>
          </div>
        </div>
      )}

      {apply.error && <ErrorBanner error={apply.error} onDismiss={apply.clearError} />}

      <p className="field-hint" style={{ marginTop: 0, marginBottom: 'var(--space-4)' }}>
        {/* `address` is what distinguishes a real article from the built-in
            sample: only a real one has somewhere to serve images from. */}
        {s.thumbnailsHint(article.address !== null)}
      </p>

      <div className="theme-grid">
        {SITE_THEME_OPTIONS.map((option) => (
          <ThemePreview
            key={option.id}
            option={option}
            article={article}
            // In the language the page is in, as a reader of it would see it (S49).
            articlesTitle={articlesHeading(settings, language.code).title}
            selected={previewing === option.id}
            current={settings.theme === option.id}
            // Clicking the saved theme is "stop previewing", not a preview of itself.
            onSelect={() => setPreviewTheme(option.id === settings.theme ? null : option.id)}
          />
        ))}
      </div>

      <div className="button-row">
        <button
          type="button"
          className="button button-primary"
          onClick={() => void applyTheme()}
          disabled={!dirty || writing}
        >
          {apply.running ? s.applying : s.applyTheme}
        </button>
        {dirty && (
          <span className="faint">
            {s.previewing(siteThemeLabel(previewing), siteThemeLabel(settings.theme))}
          </span>
        )}
      </div>
    </section>
  );
}
